import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { policies, type PolicyDetail, type PolicyParty } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatDate,
  formatDateTime,
  formatMoney,
  formatRate,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { CopyButton } from '@/shared/ui/CopyButton';
import {
  DetailCard,
  DetailError,
  DetailLoading,
  DetailTabs,
} from '@/shared/ui/DetailLayout';
import { PageHeader } from '@/shared/ui/PageHeader';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { buttonClassName } from '@/shared/ui/buttonStyles';

import { EndorsementRequestDialog } from '../aftercare/EndorsementRequestDialog';
import {
  CancellationRequestDialog,
  ClaimRegisterDialog,
} from '../aftercare/PolicyRequests';
import { DocumentsPanel } from '../documents/DocumentsPanel';

import {
  descricaoDaParcela,
  documentoOuVazio,
  enderecoDaParte,
  NAO_DEFINIDO,
  numeroDaApolice,
  ouNaoDefinido,
  simNao,
  vigenciaDe,
} from './apolice';
import { BilletsDialog } from './BilletsDialog';
import { ramoDaApolice, rotuloDaAssinatura } from './laudo';
import { PolicyDocumentCard } from './PolicyDocumentCard';
import { PolicyDownloadsMenu } from './PolicyDownloadsMenu';
import { PolicyHistoryTab } from './PolicyHistoryTab';
import { PolicyInstallmentsDialog } from './PolicyInstallmentsDialog';

/** As abas do detalhe da apolice do OnPoint, nesta ordem (APO-057 a APO-059). */
const abasDaApolice = [
  { key: 'dados-gerais', title: 'Dados Gerais' },
  { key: 'historico', title: 'Historico' },
  { key: 'dados-cadastrais', title: 'Dados Cadastrais' },
  { key: 'documentos', title: 'Documentos' },
] as const;

type AbaDaApolice = (typeof abasDaApolice)[number]['key'];

/**
 * Detalhe da apolice (`/apolices/:policyId` e `/fiancas/:policyId`, P-APO-1), com as
 * abas do OnPoint e, em Dados Gerais, as secoes na ordem dele: Numero da apolice,
 * Dados do Tomador, Dados do Segurado, Dados do Corretor, Dados da Apolice,
 * Modalidades, Coberturas Adicionais, Premio e Configuracao.
 *
 * As partes sao a fotografia da emissao: o cadastro mudar depois nao muda o que a
 * apolice diz. Os botoes Registrar sinistro, Solicitar endosso e Solicitar
 * cancelamento, que ficavam no modal da lista, moram aqui.
 */
export function PolicyDetailPage() {
  const { policyId = '' } = useParams();
  const navigate = useNavigate();
  const [parametros, setParametros] = useSearchParams();
  const aba: AbaDaApolice =
    abasDaApolice.find((item) => item.key === parametros.get('aba'))?.key ??
    'dados-gerais';

  const consulta = useQuery({
    queryKey: ['policy', policyId],
    queryFn: ({ signal }) => policies.detail(policyId, signal),
    enabled: policyId !== '',
  });

  if (consulta.isPending) return <DetailLoading />;
  if (consulta.isError) {
    return (
      <DetailError
        title="Apolice"
        alertTitle="Nao foi possivel abrir a apolice"
        message={consulta.error.message}
        backLabel="Lista de apolices"
        onBack={() => void navigate('/apolices')}
      />
    );
  }

  return (
    <ApoliceAberta
      policy={consulta.data}
      aba={aba}
      onAba={(chave) => {
        const proximos = new URLSearchParams(parametros);
        proximos.set('aba', chave);
        setParametros(proximos, { replace: true });
      }}
    />
  );
}

function ApoliceAberta({
  policy,
  aba,
  onAba,
}: {
  policy: PolicyDetail;
  aba: AbaDaApolice;
  onAba: (chave: AbaDaApolice) => void;
}) {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const [solicitacao, setSolicitacao] = useState<
    'endosso' | 'cancelamento' | 'sinistro' | 'boletos' | 'parcelas' | null
  >(null);

  // A fianca abre pela carteira de fiancas e volta para ela.
  const lista = pathname.startsWith('/fiancas') ? '/fiancas' : '/apolices';
  const numero = numeroDaApolice(policy);

  return (
    <div className="flex flex-col gap-5">
      <nav aria-label="Trilha" className="text-xs text-ink-500">
        <Link to={lista} className="text-brand-600 underline-offset-2 hover:underline">
          Lista de apolices
        </Link>{' '}
        / Apolice {numero}
      </nav>

      <PageHeader
        title={`Apolice ${numero}`}
        description={
          policy.parties?.policyholder?.legal_name ?? policy.policyholder.label
        }
        meta={<StatusBadge kind="apolice" value={policy.status} />}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PolicyDownloadsMenu
              policy={policy}
              size="md"
              onBillets={() => setSolicitacao('boletos')}
            />
            <Link
              to={`/operacoes/${policy.operation_id}`}
              className={buttonClassName({ variant: 'secondary', size: 'md' })}
            >
              Ver a operacao
            </Link>
            {can('sinistros.sinistro.create') && policy.status !== 'cancelada' ? (
              <Button variant="secondary" onClick={() => setSolicitacao('sinistro')}>
                Registrar sinistro
              </Button>
            ) : null}
            {can('endossos.endosso.create') && policy.status === 'vigente' ? (
              <Button variant="secondary" onClick={() => setSolicitacao('endosso')}>
                Solicitar endosso
              </Button>
            ) : null}
            {can('cancelamentos.cancelamento.create') &&
            policy.status !== 'cancelada' ? (
              <Button variant="danger" onClick={() => setSolicitacao('cancelamento')}>
                Solicitar cancelamento
              </Button>
            ) : null}
          </div>
        }
      />

      {policy.status === 'cancelada' ? (
        <Alert tone="danger" title="Apolice cancelada">
          {policy.cancel_reason}
        </Alert>
      ) : null}

      <DetailTabs
        label="Abas da apolice"
        tabs={[...abasDaApolice]}
        active={aba}
        onChange={onAba}
      />

      {aba === 'dados-gerais' ? (
        <DadosGerais
          policy={policy}
          onParcelas={
            can('apolices.parcela.update') && policy.status !== 'cancelada'
              ? () => setSolicitacao('parcelas')
              : undefined
          }
        />
      ) : null}
      {aba === 'historico' ? <PolicyHistoryTab policy={policy} /> : null}
      {aba === 'dados-cadastrais' ? <DadosCadastrais policy={policy} /> : null}
      {aba === 'documentos' ? (
        <DocumentsPanel
          ownerType="apolice"
          ownerId={policy.id}
          title="Documentos da apolice"
          group="Apolice"
          allowDelete
        />
      ) : null}

      {solicitacao === 'endosso' ? (
        <EndorsementRequestDialog
          policy={policy}
          open
          onClose={() => setSolicitacao(null)}
        />
      ) : null}
      {solicitacao === 'cancelamento' ? (
        <CancellationRequestDialog
          policy={policy}
          open
          onClose={() => setSolicitacao(null)}
        />
      ) : null}
      {solicitacao === 'sinistro' ? (
        <ClaimRegisterDialog
          policy={policy}
          open
          onClose={() => setSolicitacao(null)}
        />
      ) : null}
      <BilletsDialog
        policy={policy}
        open={solicitacao === 'boletos'}
        onClose={() => setSolicitacao(null)}
      />
      {solicitacao === 'parcelas' ? (
        <PolicyInstallmentsDialog
          policy={policy}
          open
          onClose={() => setSolicitacao(null)}
        />
      ) : null}
    </div>
  );
}

// --- Dados Gerais -----------------------------------------------------------------

function DadosGerais({
  policy,
  onParcelas,
}: {
  policy: PolicyDetail;
  onParcelas?: (() => void) | undefined;
}) {
  const numero = numeroDaApolice(policy);
  const basica = policy.coverages.find((c) => c.kind === 'basica');
  const adicionais = policy.coverages.filter((c) => c.kind === 'adicional');

  return (
    <div className="flex flex-col gap-5">
      <DetailCard title="Numero da apolice">
        <Grade>
          <Dado rotulo="Numero da apolice">
            <span className="flex items-center gap-1">
              {numero}
              <CopyButton value={numero} label="Copiar numero da apolice" />
            </span>
          </Dado>
          <Dado rotulo="Numero interno">{String(policy.number)}</Dado>
          <Dado rotulo="Cotacao">
            {policy.proposal_number ? (
              <span className="flex items-center gap-1">
                {policy.proposal_number}
                <CopyButton
                  value={policy.proposal_number}
                  label="Copiar numero da cotacao"
                />
              </span>
            ) : (
              NAO_DEFINIDO
            )}
          </Dado>
        </Grade>
      </DetailCard>

      <ParteCard
        titulo="Dados do Tomador"
        papel="Tomador"
        parte={policy.parties?.policyholder}
      />
      <ParteCard
        titulo="Dados do Segurado"
        papel="Segurado"
        parte={policy.parties?.insured}
      />
      <ParteCard
        titulo="Dados do Corretor"
        papel="Corretor"
        parte={policy.parties?.broker}
        corretor
      />

      <DetailCard title="Dados da Apolice">
        <Grade>
          {/* Numero longo, ramo, processo e versao do documento (P-APO-2). */}
          <DadosDaEmissao policy={policy} />
          <Dado rotulo="Emissor">{policy.issuer.label}</Dado>
          <Dado rotulo="Produto">{policy.product.label}</Dado>
          <Dado rotulo="Emissao">{formatDate(policy.issued_at)}</Dado>
          <Dado rotulo="Emitida por">{ouNaoDefinido(policy.issued_label)}</Dado>
          <Dado rotulo="Ref. da seguradora">
            {policy.insurer_reference ? (
              <span className="flex items-center gap-1">
                {policy.insurer_reference}
                <CopyButton
                  value={policy.insurer_reference}
                  label="Copiar referencia da seguradora"
                />
              </span>
            ) : (
              NAO_DEFINIDO
            )}
          </Dado>
          <Dado rotulo="Vigencia atual">
            {vigenciaDe(policy.coverage_start, policy.coverage_end)}
          </Dado>
          <Dado rotulo="Renovacao">
            {policy.renewal_status
              ? statusLabel('renovacao', policy.renewal_status)
              : NAO_DEFINIDO}
          </Dado>
          <Dado rotulo="Tomador nomeado para a corretora">
            {simNao(policy.nominated_to_broker)}
          </Dado>
        </Grade>
      </DetailCard>

      {/* Documento da apolice e filial da seguradora (P-MIN-2). */}
      <PolicyDocumentCard policy={policy} />

      <DetailCard title="Modalidades">
        <Grade>
          <Dado rotulo="Modalidade principal">{policy.modality.label}</Dado>
          <Dado rotulo="Importancia segurada principal">
            {formatMoney(policy.insured_amount)}
          </Dado>
          <Dado rotulo="Importancia segurada na emissao">
            {policy.original_insured_amount
              ? formatMoney(policy.original_insured_amount)
              : NAO_DEFINIDO}
            {policy.insured_amount_changed ? ' (alterada por endosso)' : ''}
          </Dado>
          <Dado rotulo="Premio">
            {policy.principal_premium
              ? formatMoney(policy.principal_premium)
              : NAO_DEFINIDO}
          </Dado>
          <Dado rotulo="Vigencia principal">
            {vigenciaDe(
              basica?.coverage_start ?? policy.coverage_start,
              basica?.coverage_end ?? policy.coverage_end,
            )}
          </Dado>
          <Dado rotulo="Prazo da vigencia principal">
            {policy.term_days ? `${policy.term_days} dias` : NAO_DEFINIDO}
          </Dado>
          <Dado rotulo="Modalidade complementar">
            {policy.complementary_modality?.label ?? NAO_DEFINIDO}
          </Dado>
          <Dado rotulo="Premio da complementar">
            {policy.complementary_premium
              ? formatMoney(policy.complementary_premium)
              : NAO_DEFINIDO}
          </Dado>
          <Dado rotulo="Vigencia da complementar">
            {policy.complementary_coverage_start
              ? vigenciaDe(
                  policy.complementary_coverage_start,
                  policy.complementary_coverage_end,
                )
              : NAO_DEFINIDO}
          </Dado>
        </Grade>
      </DetailCard>

      <DetailCard title="Coberturas Adicionais">
        {adicionais.length === 0 ? (
          <p className="text-sm text-ink-600">A apolice nao tem cobertura adicional.</p>
        ) : (
          <ul className="divide-y divide-line">
            {adicionais.map((cobertura) => (
              <li key={cobertura.id} className="grid gap-3 py-2 sm:grid-cols-4">
                <Dado rotulo="Nome da cobertura">{cobertura.name}</Dado>
                <Dado rotulo="Valor">
                  {cobertura.insured_amount
                    ? formatMoney(cobertura.insured_amount)
                    : NAO_DEFINIDO}
                </Dado>
                <Dado rotulo="Premio">
                  {cobertura.premium ? formatMoney(cobertura.premium) : NAO_DEFINIDO}
                </Dado>
                <Dado rotulo="Vigencia">
                  {vigenciaDe(cobertura.coverage_start, cobertura.coverage_end)}
                </Dado>
              </li>
            ))}
          </ul>
        )}
      </DetailCard>

      <DetailCard
        title="Premio"
        action={
          onParcelas ? (
            <Button size="sm" variant="secondary" onClick={onParcelas}>
              Informar parcelas
            </Button>
          ) : undefined
        }
      >
        <Grade>
          <Dado rotulo="Premio total">{formatMoney(policy.premium)}</Dado>
          <Dado rotulo="Numero de parcelas">{String(policy.installments.length)}</Dado>
          <Dado rotulo="Taxa aplicada (%)">{formatRate(policy.applied_rate_bps)}</Dado>
          <Dado rotulo="Valor da comissao">
            {policy.commission_amount
              ? formatMoney(policy.commission_amount)
              : NAO_DEFINIDO}
          </Dado>
          <Dado rotulo="Comissao (%)">{formatRate(policy.commission_bps)}</Dado>
          <Dado rotulo="Soma das parcelas informadas">
            {policy.installments.length > 0
              ? formatMoney(policy.installments_total)
              : NAO_DEFINIDO}
          </Dado>
        </Grade>
        {policy.installments.length > 0 ? (
          <div>
            <p className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
              Parcelas
            </p>
            <ul className="mt-1 flex flex-col gap-1 text-sm text-ink-800">
              {policy.installments.map((parcela) => (
                <li key={parcela.id} className="flex flex-wrap items-center gap-2">
                  <span>
                    {descricaoDaParcela(
                      parcela.number,
                      parcela.amount,
                      parcela.interest,
                    )}
                  </span>
                  <span className="text-ink-500">
                    vence em {formatCivilDate(parcela.due_on)}
                  </span>
                  <StatusBadge kind="parcela" value={parcela.status} />
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-xs text-ink-500">
            Nenhuma parcela informada. A emissao copia as parcelas do financeiro da
            cotacao; sem elas, quem tem a alcada informa.
          </p>
        )}
      </DetailCard>

      <DetailCard title="Configuracao">
        <div className="flex flex-wrap gap-4">
          <CaixaDeLeitura rotulo="Retoma de objeto (Step-in)" valor={policy.step_in} />
          <CaixaDeLeitura rotulo="Monitoramento" valor={policy.monitoring} />
          <CaixaDeLeitura rotulo="Cosseguro" valor={policy.has_coinsurance} />
          <CaixaDeLeitura rotulo="Grande risco" valor={policy.big_risk} />
        </div>
        {policy.coinsurance_type ? (
          <p className="text-xs text-ink-600">
            Tipo de cosseguro:{' '}
            {policy.coinsurance_type === 'cedido' ? 'cedido' : 'aceito'}
          </p>
        ) : null}
        {policy.coinsurers.length > 0 ? (
          <ul className="flex flex-col gap-1 text-sm">
            {policy.coinsurers.map((cosseguradora) => (
              <li key={cosseguradora.id}>
                {cosseguradora.name} - {formatRate(cosseguradora.share_bps)}
                {cosseguradora.is_leader ? ' (lider)' : ''}
              </li>
            ))}
          </ul>
        ) : null}
      </DetailCard>
    </div>
  );
}

// --- Dados Cadastrais -------------------------------------------------------------

/**
 * Dados Cadastrais: as partes como a apolice as guardou, com a origem da fotografia.
 * No OnPoint as duas abas mostraram os mesmos campos (A CONFIRMAR, APO-059); aqui ela
 * diz de onde veio cada retrato e leva ao cadastro atual.
 */
function DadosCadastrais({ policy }: { policy: PolicyDetail }) {
  const cadastros: {
    papel: string;
    parte: PolicyParty | null | undefined;
    caminho: string | null;
  }[] = [
    {
      papel: 'Tomador',
      parte: policy.parties?.policyholder,
      caminho: `/tomadores/${policy.policyholder.id}`,
    },
    {
      papel: 'Segurado',
      parte: policy.parties?.insured,
      caminho: policy.insured ? `/segurados/${policy.insured.id}` : null,
    },
    {
      papel: 'Corretor',
      parte: policy.parties?.broker,
      caminho: policy.broker ? `/corretores/${policy.broker.id}` : null,
    },
  ];
  return (
    <div className="flex flex-col gap-5">
      {cadastros.map(({ papel, parte, caminho }) => (
        <DetailCard key={papel} title={`Dados do ${papel}`}>
          <Grade>
            <Dado rotulo={`Razao social do ${papel.toLowerCase()}`}>
              {parte?.legal_name ?? NAO_DEFINIDO}
            </Dado>
            <Dado rotulo="Documento">
              {parte ? documentoOuVazio(parte.document) || NAO_DEFINIDO : NAO_DEFINIDO}
            </Dado>
            <Dado rotulo="Endereco">{enderecoDaParte(parte)}</Dado>
            <Dado rotulo="Origem da fotografia">
              {parte
                ? `${parte.source === 'carga' ? 'Cadastro no dia da carga' : parte.source === 'endosso' ? 'Endosso' : 'Emissao'} em ${formatDateTime(parte.captured_at)}`
                : NAO_DEFINIDO}
            </Dado>
          </Grade>
          {caminho ? (
            <Link
              to={caminho}
              className="text-sm text-brand-600 underline-offset-2 hover:underline"
            >
              Ver o cadastro atual
            </Link>
          ) : null}
        </DetailCard>
      ))}
    </div>
  );
}

// --- Apoio --------------------------------------------------------------------------

function ParteCard({
  titulo,
  papel,
  parte,
  corretor = false,
}: {
  titulo: string;
  papel: string;
  parte: PolicyParty | null | undefined;
  corretor?: boolean;
}) {
  return (
    <DetailCard title={titulo}>
      {parte ? (
        <Grade>
          <Dado rotulo={`Razao social do ${papel}`}>{parte.legal_name}</Dado>
          <Dado rotulo={`CNPJ do ${papel}`}>
            {parte.document ? (
              <span className="flex items-center gap-1">
                {documentoOuVazio(parte.document)}
                <CopyButton
                  value={parte.document}
                  label={`Copiar documento do ${papel}`}
                />
              </span>
            ) : (
              NAO_DEFINIDO
            )}
          </Dado>
          {corretor ? (
            <Dado rotulo="Codigo SUSEP">{ouNaoDefinido(parte.susep_code)}</Dado>
          ) : (
            <Dado rotulo={`Endereco do ${papel}`}>{enderecoDaParte(parte)}</Dado>
          )}
          {/* Nome social e nome fantasia que o endosso neutro informou (P-END-1). */}
          {!corretor && parte.social_name ? (
            <Dado rotulo={`Nome social do ${papel}`}>{parte.social_name}</Dado>
          ) : null}
          {!corretor && parte.trade_name ? (
            <Dado rotulo={`Nome fantasia do ${papel}`}>{parte.trade_name}</Dado>
          ) : null}
        </Grade>
      ) : (
        <p className="text-sm text-ink-600">
          {corretor ? 'Operacao direta, sem corretor.' : `${NAO_DEFINIDO}.`}
        </p>
      )}
    </DetailCard>
  );
}

function Grade({ children }: { children: ReactNode }) {
  return <dl className="grid gap-4 sm:grid-cols-3">{children}</dl>;
}

/**
 * Dados da apolice que vem da seguradora ou do produto (P-APO-2): o numero longo, que
 * a seguradora da e o SeguraCore nao gera, o ramo e o processo SUSEP copiados na
 * emissao e a versao do documento.
 */
function DadosDaEmissao({ policy }: { policy: PolicyDetail }) {
  return (
    <>
      <Dado rotulo="Numero longo da apolice">
        {policy.long_number ? (
          <span className="flex items-center gap-1">
            {policy.long_number}
            <CopyButton
              value={policy.long_number}
              label="Copiar numero longo da apolice"
            />
          </span>
        ) : (
          NAO_DEFINIDO
        )}
      </Dado>
      <Dado rotulo="Ramo">{ramoDaApolice(policy) ?? NAO_DEFINIDO}</Dado>
      <Dado rotulo="Processo SUSEP">{ouNaoDefinido(policy.susep_process_code)}</Dado>
      <Dado rotulo="Versao do documento">
        {rotuloDaAssinatura(policy.document_signature_status) ?? NAO_DEFINIDO}
      </Dado>
    </>
  );
}

function Dado({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        {rotulo}
      </dt>
      <dd className="mt-0.5 text-sm font-medium break-words text-ink-900">
        {children}
      </dd>
    </div>
  );
}

function CaixaDeLeitura({
  rotulo,
  valor,
}: {
  rotulo: string;
  valor: boolean | undefined;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-ink-700">
      <input
        type="checkbox"
        checked={valor ?? false}
        readOnly
        disabled
        className="size-4 rounded border-line-strong"
      />
      {rotulo}
    </label>
  );
}
